import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * App-wide hover tooltip engine.
 *
 * Watches (via event delegation) for any element carrying a
 * `data-tooltip-content` attribute and shows a dark text bubble above it
 * (below it when the element sits near the top of the viewport).
 *
 * The bubble is measured after render and clamped to the viewport, so it never
 * runs off the left/right edge; the arrow shifts to keep pointing at the
 * element. Portalled to <body> with position:fixed — immune to scroll
 * containers and transformed ancestors. Mount it once.
 */
const GAP = 10;
const MARGIN = 8;
const ARROW = 6;
const ARROW_INSET = 12;

const CustomTooltip = () => {
	const [tip, setTip] = useState(null);   // { text, cx, top, bottom, below }
	const [pos, setPos] = useState(null);   // { left, top, arrowX }
	const bubbleRef = useRef(null);

	useEffect(() => {
		const findAnchor = (node) =>
			node && node.closest ? node.closest('[data-tooltip-content]') : null;

		const handleOver = (e) => {
			const anchor = findAnchor(e.target);
			if (!anchor) return;
			const text = anchor.getAttribute('data-tooltip-content');
			if (!text) return;
			const r = anchor.getBoundingClientRect();
			setTip({
				text,
				cx: r.left + r.width / 2,
				top: r.top,
				bottom: r.bottom,
				below: r.top < 90,
			});
		};

		const handleOut = (e) => {
			const anchor = findAnchor(e.target);
			if (!anchor) return;
			// Ignore moves that stay inside the same anchor element.
			if (e.relatedTarget && anchor.contains(e.relatedTarget)) return;
			setTip(null);
		};

		document.addEventListener('mouseover', handleOver);
		document.addEventListener('mouseout', handleOut);
		return () => {
			document.removeEventListener('mouseover', handleOver);
			document.removeEventListener('mouseout', handleOut);
		};
	}, []);

	// Measure the rendered bubble, then clamp it to the viewport and place the
	// arrow on the anchor. Runs before paint, so the user never sees the
	// pre-clamp position.
	useLayoutEffect(() => {
		if (!tip || !bubbleRef.current) {
			setPos(null);
			return;
		}
		const b = bubbleRef.current.getBoundingClientRect();
		const left = Math.max(
			MARGIN,
			Math.min(tip.cx - b.width / 2, window.innerWidth - MARGIN - b.width)
		);
		const top = tip.below ? tip.bottom + GAP : tip.top - GAP - b.height;
		const arrowX = Math.max(
			ARROW_INSET,
			Math.min(tip.cx - left, b.width - ARROW_INSET)
		);
		setPos({ left, top, arrowX });
	}, [tip]);

	if (!tip) return null;

	const ready = pos !== null;
	const arrowEdge = tip.below
		? { top: -ARROW, borderBottom: `${ARROW}px solid #1f2937` }
		: { bottom: -ARROW, borderTop: `${ARROW}px solid #1f2937` };

	return createPortal(
		<div
			ref={bubbleRef}
			className='fixed z-[9999] pointer-events-none'
			style={{
				left: ready ? pos.left : 0,
				top: ready ? pos.top : 0,
				visibility: ready ? 'visible' : 'hidden',
			}}
		>
			<div className='relative max-w-[300px] rounded-md bg-gray-800 px-3 py-2 text-[13px] leading-snug text-gray-50 shadow-lg'>
				{tip.text}
				<span
					aria-hidden
					className='absolute w-0 h-0'
					style={{
						left: ready ? pos.arrowX : '50%',
						marginLeft: -ARROW,
						borderLeft: `${ARROW}px solid transparent`,
						borderRight: `${ARROW}px solid transparent`,
						...arrowEdge,
					}}
				/>
			</div>
		</div>,
		document.body
	);
};

export default CustomTooltip;
